import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as studentCategoryService from "../service/studentCategoryService";
import type { StudentCategory, CreateCategoryPayload, UpdateCategoryPayload } from "../service/studentCategoryService";

interface StudentCategoryState {
  categories: StudentCategory[];
  activeCategories: StudentCategory[];
  loading: boolean;
  submitting: boolean;
  error: string | null;
}

const initialState: StudentCategoryState = {
  categories: [],
  activeCategories: [],
  loading: false,
  submitting: false,
  error: null,
};

export const loadCategories = createAsyncThunk("studentCategories/loadAll", async () => {
  return await studentCategoryService.fetchCategories();
});

export const loadActiveCategories = createAsyncThunk("studentCategories/loadActive", async () => {
  return await studentCategoryService.fetchActiveCategories();
});

export const addCategory = createAsyncThunk(
  "studentCategories/add",
  async (payload: CreateCategoryPayload) => {
    return await studentCategoryService.createCategory(payload);
  }
);

export const editCategory = createAsyncThunk(
  "studentCategories/edit",
  async ({ id, payload }: { id: number; payload: UpdateCategoryPayload }) => {
    return await studentCategoryService.updateCategory(id, payload);
  }
);

export const disableCategory = createAsyncThunk(
  "studentCategories/deactivate",
  async (id: number, { dispatch }) => {
    await studentCategoryService.deactivateCategory(id);
    await dispatch(loadCategories());
  }
);

export const enableCategory = createAsyncThunk(
  "studentCategories/reactivate",
  async (id: number, { dispatch }) => {
    await studentCategoryService.reactivateCategory(id);
    await dispatch(loadCategories());
  }
);

const studentCategorySlice = createSlice({
  name: "studentCategories",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadCategories.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(loadCategories.fulfilled, (state, action) => { state.loading = false; state.categories = action.payload; })
      .addCase(loadCategories.rejected, (state, action) => { state.loading = false; state.error = action.error.message ?? "Failed to load categories"; })

      .addCase(loadActiveCategories.fulfilled, (state, action) => { state.activeCategories = action.payload; })

      .addCase(addCategory.pending, (state) => { state.submitting = true; state.error = null; })
      .addCase(addCategory.fulfilled, (state, action) => { state.submitting = false; state.categories.push(action.payload); })
      .addCase(addCategory.rejected, (state, action) => { state.submitting = false; state.error = action.error.message ?? "Failed to add category"; })

      .addCase(editCategory.pending, (state) => { state.submitting = true; state.error = null; })
      .addCase(editCategory.fulfilled, (state, action) => {
        state.submitting = false;
        const idx = state.categories.findIndex((c) => c.id === action.payload.id);
        if (idx !== -1) state.categories[idx] = action.payload;
      })
      .addCase(editCategory.rejected, (state, action) => { state.submitting = false; state.error = action.error.message ?? "Failed to update category"; })

      .addCase(disableCategory.rejected, (state, action) => { state.error = action.error.message ?? "Failed to deactivate category"; })
      .addCase(enableCategory.rejected, (state, action) => { state.error = action.error.message ?? "Failed to reactivate category"; });
  },
});

export default studentCategorySlice.reducer;